import { Link } from "react-router-dom";
import { 
  ArrowRight, CheckCircle2, FileCheck, Bot, Calculator, 
  Receipt, Users, BarChart3, Clock, Activity,
  ThumbsUp, Download, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { CTASection } from "@/components/shared/CTASection";
import { SEO } from "@/components/seo/SEO";

const steps = [
  {
    number: "01",
    icon: Download,
    title: "Coleta e processamento de faturas",
    description: "Coletamos e processamos faturas das UCs e UGs via e-mail, portal da distribuidora ou upload manual. Gestão de status com quarentena, erros e campos desconhecidos.",
    color: "from-blue-500 to-cyan-500",
  },
  {
    number: "02",
    icon: FileCheck,
    title: "Auditorias automáticas",
    description: "Os dados extraídos passam por auditorias automáticas, ficando organizados na plataforma. Validação de saldo, injeção e compensado com destaque de divergências.",
    color: "from-indigo-500 to-violet-500",
  },
  {
    number: "03",
    icon: Calculator,
    title: "Cálculo das cobranças",
    description: "Calculamos os valores das cobranças de acordo com suas regras de aluguel parametrizadas. Dezenas de regras já cadastradas com fórmulas complexas e personalizáveis.",
    color: "from-violet-500 to-purple-500",
  },
  {
    number: "04",
    icon: ThumbsUp,
    title: "Revisão e aprovação",
    description: "Sua equipe pode revisar e aprovar as pré-faturas antes da emissão final. Fluxo de aprovação com visibilidade completa dos valores calculados.",
    color: "from-purple-500 to-pink-500",
  },
  {
    number: "05",
    icon: Receipt,
    title: "Emissão e envio de boletos",
    description: "Emitimos e enviamos automaticamente seus boletos para seus clientes. Faturas customizáveis com sua identidade visual. Acompanhamento de pagamentos até a baixa.",
    color: "from-emerald-500 to-teal-500",
  },
];

const homologatedBanks = [
  "ASAAS", "Banco do Brasil", "Cora", "Inter", "Santander", "Itaú", "Sicredi", "Sicoob"
];

const implantationChecklist = [
  { item: "Levantamento de usinas, UCs e UGs", time: "Semana 1" },
  { item: "Configuração de contratos e regras de aluguel", time: "Semana 1-2" },
  { item: "Ativação de robôs e integração com distribuidoras", time: "Semana 2" },
  { item: "Importação de faturas históricas", time: "Semana 2-3" },
  { item: "Treinamento via Universidade COGNI", time: "Semana 3" },
  { item: "Go-live e acompanhamento", time: "Semana 3-4" },
];

export default function ComoFunciona() {
  return (
    <Layout>
      <SEO path="/como-funciona" title="Como Funciona — Cogni" description="Entenda como a Cogni automatiza a gestão de créditos de geração distribuída, da geração até o repasse ao consumidor." />
      {/* HERO */}
      <section className="gradient-hero py-14 sm:py-20 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 sm:w-64 h-48 sm:h-64 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 sm:mb-6">
              Ciclo de faturamento
            </span>
            <h1 className="text-display font-bold mb-4 sm:mb-6 text-balance">
              Como a Cogni apoia seu ciclo de faturamento
            </h1>
            <p className="text-body-lg text-muted-foreground mb-6 sm:mb-8">
              Mais simplicidade, visibilidade e eficiência nas etapas do seu ciclo de faturamento da operação de créditos de energia.
            </p>
            <Link to="/contato">
              <Button size="lg" className="gradient-primary text-primary-foreground px-8 glow-primary touch-target">
                Agendar demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESSO EM 5 PASSOS */}
      <section className="section-padding section-alt">
        <div className="container-wide">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-headline font-bold mb-4">
              Ciclo de faturamento em 5 passos
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Uma visão clara de como a operação funciona dentro da plataforma.
            </p>
          </div>

          {/* Timeline vertical */}
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-5 sm:left-6 md:left-8 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-emerald-500 rounded-full" />

              <div className="space-y-6 sm:space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="relative flex gap-4 sm:gap-6 md:gap-8">
                    <div className={`relative z-10 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center font-bold text-sm sm:text-lg md:text-xl flex-shrink-0 shadow-lg`}>
                      {step.number}
                    </div>
                    <div className="flex-1 pb-6 sm:pb-8">
                      <div className="bg-background border border-border rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center`}>
                            <step.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          </div>
                          <h3 className="text-base sm:text-lg md:text-xl font-bold">{step.title}</h3>
                        </div>
                        <p className="text-muted-foreground text-sm sm:text-base">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bancos homologados */}
          <div className="mt-10 sm:mt-14 max-w-4xl mx-auto text-center">
            <p className="text-muted-foreground text-xs sm:text-sm mb-4">Bancos homologados para emissão de boletos</p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {homologatedBanks.map((bank, i) => (
                <div key={i} className="bg-muted/50 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm border border-border">
                  {bank}
                </div>
              ))}
            </div>
          </div>

          {/* Fluxo visual simplificado */}
          <div className="mt-12 sm:mt-16 max-w-5xl mx-auto">
            <div className="section-gradient-purple rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4 relative z-10">
                {steps.map((step, index) => (
                  <div key={index} className="text-center group">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-lg group-hover:scale-110 transition-transform`}>
                      <step.icon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                    </div>
                    <p className="text-[11px] sm:text-xs md:text-sm font-medium">{step.title.split(' ').slice(0, 2).join(' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPLANTAÇÃO */}
      <section className="section-padding section-warm">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
                </div>
              </div>
              <h2 className="text-headline font-bold mb-4">Implantação</h2>
              <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
                Acompanhamos você desde o levantamento inicial até o go-live. 
                Treinamento realizado via Universidade COGNI com vídeos sob demanda.
              </p>
            </div>

            <div className="bg-background border border-border rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 divide-y divide-border">
                {implantationChecklist.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3.5 sm:p-4 md:p-5 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                      </div>
                      <span className="font-medium text-sm sm:text-base">{item.item}</span>
                    </div>
                    <span className="text-muted-foreground text-xs sm:text-sm bg-muted px-2 sm:px-3 py-1 rounded-full ml-2 flex-shrink-0">
                      {item.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>


            <div className="mt-6 sm:mt-8 text-center">
              <Link to="/contato">
                <Button variant="outline" size="lg" className="touch-target">
                  <FileCheck className="mr-2 h-5 w-5" />
                  Solicitar análise
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTADO */}
      <section className="section-padding section-gradient-blue relative overflow-hidden">
        <div className="absolute top-0 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="container-narrow text-center relative z-10">
          <h2 className="text-headline font-bold mb-4 sm:mb-6">
            Resultado: operação com mais consistência
          </h2>
          <p className="text-body-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Com a Cogni, sua empresa passa a operar com mais eficiência, confiabilidade e transparência, 
            reduzindo dependência de controles manuais e ganhando mais capacidade de escala.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-background/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-border shadow-lg">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <BarChart3 className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div className="font-semibold text-base sm:text-lg mb-1">Menos trabalho manual</div>
              <div className="text-sm text-muted-foreground">Robôs, IA e cálculos automatizados</div>
            </div>
            <div className="bg-background/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-border shadow-lg">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Activity className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div className="font-semibold text-base sm:text-lg mb-1">Mais confiabilidade</div>
              <div className="text-sm text-muted-foreground">Rastreabilidade e auditoria automática</div>
            </div>
            <div className="bg-background/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-border shadow-lg">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Users className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div className="font-semibold text-base sm:text-lg mb-1">Mais transparência</div>
              <div className="text-sm text-muted-foreground">Áreas do consumidor e investidor</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <CTASection
        variant="dark"
        title="Veja funcionando na prática"
        subtitle="Agende uma demonstração gratuita com nosso time e veja a Cogni operando com dados reais."
      />
    </Layout>
  );
}
